import * as dateMath from 'date-arithmetic';

export const TITLE_CELL_WIDTH_PX = 180;

export type Period = { start:Date, end:Date }
export type GraphPeriod = { startMonthYear: Date, months: number }
export type PeriodGraphics = { offsetPx: number, widthPx: number }


export function graphHelper(graphPeriod: GraphPeriod, graphWidthPx: number) {
    if (!Number.isInteger(graphPeriod.months) || graphPeriod.months < 1)
        throw new Error('invalid months');

    function getGraphBounds() {
        const start = new Date(graphPeriod.startMonthYear.getFullYear(), graphPeriod.startMonthYear.getMonth(), 1);
        const end = dateMath.add(
            dateMath.add(start, graphPeriod.months, 'month'),
            -1,
            'milliseconds'
        );
        return { start, end };
    }

    return {

        getBarSpec(eventPeriod: Period):PeriodGraphics | null 
        {
            const spec = calculateSpec(getGraphBounds(), graphWidthPx, eventPeriod);
            if(spec === null)
                return null;

            return spec;
        },

        getDotSpec(eventPeriod: Period, dotWidthPx: number):PeriodGraphics | null 
        {
            const spec = calculateSpec(getGraphBounds(), graphWidthPx, eventPeriod);
            if(spec === null)
                return null;

            
            let shiftedOffset = spec.offsetPx - (dotWidthPx / 2);

            if (shiftedOffset < 0) {
                shiftedOffset = 0;
            }

            return {
                offsetPx: shiftedOffset,
                widthPx: dotWidthPx
            };
        },

        detectGraphicsOverlap(a:PeriodGraphics, b:PeriodGraphics): boolean {
            const aEnd = a.offsetPx + a.widthPx;
            const bEnd = b.offsetPx + b.widthPx;
            return a.offsetPx < bEnd && b.offsetPx < aEnd;
        }
    }
}

// Offset and width needs to calculated with all months having the same pixel width in mind
function calculateSpec(graphPeriod:Period, graphWidthPx: number, event:Period):PeriodGraphics | null
{
    if(event.end <= graphPeriod.start)
        return null;

    if(event.start > graphPeriod.end)
        return null;

    if(event.start < graphPeriod.start && event.end > graphPeriod.end)
    {
        return {
            offsetPx: 0,
            widthPx: graphWidthPx
        };
    }

    const months = monthCount(graphPeriod.start, graphPeriod.end);
    const pixelPerMonth = graphWidthPx / months;

    const constrainedStart = new Date(Math.max(event.start.getTime(), graphPeriod.start.getTime()));
    const constrainedEnd = new Date(Math.min(event.end.getTime(), graphPeriod.end.getTime()));

    const startOffset = fractionalMonthOffset(constrainedStart, graphPeriod.start);
    const endOffset = fractionalMonthOffset(constrainedEnd, graphPeriod.start);

    return {
        offsetPx: startOffset * pixelPerMonth,
        widthPx: (endOffset - startOffset) * pixelPerMonth
    };
}

// Number of month cells in the graph period (start is the first of a month, end is the last millisecond of the last month)
function monthCount(start:Date, end:Date): number
{
    return (end.getFullYear() - start.getFullYear()) * 12
        + (end.getMonth() - start.getMonth())
        + 1;
}

// Position of a date in fractional months since the graph start:
// whole months plus the fraction of the current month that has elapsed.
function fractionalMonthOffset(date:Date, graphStart:Date): number
{
    const wholeMonths = (date.getFullYear() - graphStart.getFullYear()) * 12
        + (date.getMonth() - graphStart.getMonth());

    const monthStart = new Date(date.getFullYear(), date.getMonth(), 1);
    const nextMonthStart = new Date(date.getFullYear(), date.getMonth() + 1, 1);
    const fraction = (date.getTime() - monthStart.getTime()) / (nextMonthStart.getTime() - monthStart.getTime());

    return wholeMonths + fraction;
}